import { useEffect, useState } from 'react'
import type { VoiceCallEndReason, VoiceCallSession } from '../../../types/voice-call'

/**
 * The user-facing connect lifecycle for a 1:1 / group call. Several of these map
 * to a single internal `phase` ('outgoing' covers both Calling and Ringing; an
 * 'active' call can be momentarily Connected or transiently Reconnecting), so we
 * derive the richer stage here and keep the copy in ONE place for every overlay.
 */
export type CallConnectStage =
  | 'idle'
  | 'incoming'
  | 'calling'
  | 'ringing'
  | 'connecting'
  | 'connected'
  | 'in-call'
  | 'reconnecting'
  | 'ended'

export type CallConnectStatus = {
  stage: CallConnectStage
  /** Short label for the current state (e.g. "Calling…", "Reconnecting…"). */
  label: string
  /** True while an active call is re-establishing its media connection. */
  isReconnecting: boolean
  /** True once the call has ended (terminal state). */
  isTerminal: boolean
}

/** How long we show "Calling…" before assuming the far end is ringing. */
const RINGING_DELAY_MS = 2500
/** How long the "Connected" confirmation flashes before showing the duration. */
const CONNECTED_FLASH_MS = 1600

type StatusSession = Pick<
  VoiceCallSession,
  'phase' | 'mediaType' | 'endReason' | 'errorMessage' | 'disconnectDetail' | 'reconnecting' | 'callId' | 'connectedAt'
>

function terminalLabel(
  reason: VoiceCallEndReason | undefined,
  errorMessage?: string,
  disconnectDetail?: string,
): string {
  switch (reason) {
    case 'declined':
      return 'Call declined'
    case 'missed':
      return 'No answer'
    case 'busy':
      return 'Line busy'
    case 'cancelled':
      return 'Call cancelled'
    case 'interrupted':
      return disconnectDetail ?? errorMessage ?? 'Connection lost'
    case 'error':
      return errorMessage ?? 'Call failed'
    case 'consent-missing':
      return 'Call ended: recording consent needed'
    case 'completed':
    default:
      return 'Call ended'
  }
}

export function useCallConnectStatus(session: StatusSession | null | undefined): CallConnectStatus {
  const phase = session?.phase
  const callId = session?.callId
  const connectedAt = session?.connectedAt
  const [outgoingRinging, setOutgoingRinging] = useState(false)
  const [connectedFlash, setConnectedFlash] = useState(false)

  // Calling… → Ringing… while dialing out (no far-end ring signal exists, so we
  // assume the remote is ringing after a short dial delay — standard telephony UX).
  useEffect(() => {
    if (phase !== 'outgoing') {
      setOutgoingRinging(false)
      return
    }
    setOutgoingRinging(false)
    const t = setTimeout(() => setOutgoingRinging(true), RINGING_DELAY_MS)
    return () => clearTimeout(t)
  }, [phase, callId])

  // Flash "Connected" the moment the call goes active, then settle to "In call".
  useEffect(() => {
    if (phase !== 'active') {
      setConnectedFlash(false)
      return
    }
    setConnectedFlash(true)
    const t = setTimeout(() => setConnectedFlash(false), CONNECTED_FLASH_MS)
    return () => clearTimeout(t)
  }, [phase, callId, connectedAt])

  if (!session || !phase) {
    return { stage: 'idle', label: '', isReconnecting: false, isTerminal: false }
  }

  const isVideo = session.mediaType === 'video'

  if (phase === 'ended') {
    return {
      stage: 'ended',
      label: terminalLabel(session.endReason, session.errorMessage, session.disconnectDetail),
      isReconnecting: false,
      isTerminal: true,
    }
  }
  if (phase === 'incoming') {
    return {
      stage: 'incoming',
      label: isVideo ? 'Incoming video call' : 'Incoming call',
      isReconnecting: false,
      isTerminal: false,
    }
  }
  if (phase === 'outgoing') {
    return {
      stage: outgoingRinging ? 'ringing' : 'calling',
      label: outgoingRinging ? 'Ringing…' : 'Calling…',
      isReconnecting: false,
      isTerminal: false,
    }
  }
  if (phase === 'connecting') {
    return { stage: 'connecting', label: 'Connecting…', isReconnecting: false, isTerminal: false }
  }
  if (phase === 'active') {
    if (session.reconnecting) {
      return { stage: 'reconnecting', label: 'Reconnecting…', isReconnecting: true, isTerminal: false }
    }
    if (connectedFlash) {
      return { stage: 'connected', label: 'Connected', isReconnecting: false, isTerminal: false }
    }
    return {
      stage: 'in-call',
      label: isVideo ? 'In video call' : 'In call',
      isReconnecting: false,
      isTerminal: false,
    }
  }
  return { stage: 'idle', label: '', isReconnecting: false, isTerminal: false }
}
